import { useBusinessEntity } from '@commenda/api';
import {
  CountryCode,
  FullscreenModal,
  FullscreenModalContent,
  Loading,
  Switcher,
  SwitcherItem,
  SwitcherList,
} from '@commenda/kakashi';
import { IncorporationFormHeader } from '../../form-builder/components/new-incorp-layout';
import { AddCorporateShareholderForm } from './forms/AddCorporateShareholderForm';
import { AddIndividualShareholderForm } from './forms/AddIndividualShareholderForm';
import { useEntityOwnershipNouns } from './ownershipNouns';

interface AddShareholderPageProps {
  corporationId: number;
  type: 'individual' | 'entity';
  onTypeChange: (next: 'individual' | 'entity') => void;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  defaultHasSignificantControl?: boolean;
  // Closes this page and opens the edit form for the picked person's
  // shareholding when they're already on the cap table. Carries the
  // resolved `stakeholdingId` so the parent can route to `/edit/:id`.
  onEditExistingShareholder?: (stakeholdingId: string) => void;
}

export const AddShareholderPage = ({
  corporationId,
  type,
  onTypeChange,
  onClose,
  onSuccess,
  onError,
  defaultHasSignificantControl,
  onEditExistingShareholder,
}: AddShareholderPageProps) => {
  const { data: businessEntity, isLoading } = useBusinessEntity(corporationId);
  const nouns = useEntityOwnershipNouns(corporationId);

  const companyId = businessEntity?.generalCorporationInfo?.companyId;
  const entityName = businessEntity?.generalCorporationInfo?.legalName ?? '';
  const entityCountry =
    businessEntity?.generalCorporationInfo?.incorporationCountry;

  return (
    <FullscreenModal isOpen onClose={onClose} enableEscapeKey>
      <IncorporationFormHeader
        entity={
          entityName && entityCountry
            ? { name: entityName, countryCode: entityCountry as CountryCode }
            : undefined
        }
        onClose={onClose}
      />

      <FullscreenModalContent className="bg-bg-light flex justify-center overflow-y-auto">
        {isLoading || !businessEntity || !companyId ? (
          <div className="flex flex-1 items-center justify-center py-7xl">
            <Loading />
          </div>
        ) : (
          <div className="flex w-[562px] max-w-full flex-col gap-2xl py-4xl">
            <div className="flex flex-col gap-xs">
              <h1 className="display-xs-bold text-text-primary">
                Add {nouns.lowerSingular}
              </h1>
              <p className="txt-small-regular text-text-secondary">
                Add significant {nouns.lowerPlural}. Everyone else is grouped
                into &ldquo;All other {nouns.lowerPlural}.&rdquo;
              </p>
            </div>

            <Switcher
              value={type}
              onValueChange={(v) => onTypeChange(v as 'individual' | 'entity')}
            >
              <SwitcherList aria-label={`${nouns.singular} type`}>
                <SwitcherItem value="individual">Individual</SwitcherItem>
                <SwitcherItem value="entity">Entity</SwitcherItem>
              </SwitcherList>
            </Switcher>

            {type === 'individual' ? (
              <AddIndividualShareholderForm
                corporationId={corporationId}
                onCancel={onClose}
                onSuccess={onSuccess}
                onError={onError}
                defaultHasSignificantControl={defaultHasSignificantControl}
                onEditExistingShareholder={onEditExistingShareholder}
              />
            ) : (
              <AddCorporateShareholderForm
                corporationId={corporationId}
                companyId={companyId}
                onCancel={onClose}
                onSuccess={onSuccess}
                onError={onError}
              />
            )}
          </div>
        )}
      </FullscreenModalContent>
    </FullscreenModal>
  );
};
